/**
 * Error Classification Utility
 * Classifies different types of login errors for better user experience
 */

export const ERROR_TYPES = {
    AUTHENTICATION: 'AUTHENTICATION',
    NETWORK: 'NETWORK',
    SERVER: 'SERVER',
    RATE_LIMIT: 'RATE_LIMIT',
    OTP_INVALID: 'OTP_INVALID',
    OTP_EXPIRED: 'OTP_EXPIRED',
    OTP_LOCKED: 'OTP_LOCKED',
    OTP_SEND_COOLDOWN: 'OTP_SEND_COOLDOWN',
    OTP_SEND_RATE_LIMITED: 'OTP_SEND_RATE_LIMITED',
    CAPTCHA: 'CAPTCHA',
    UNKNOWN: 'UNKNOWN'
};

/**
 * Classifies error types based on error messages and codes
 * @param {string|object} error - The error message or error object
 * @returns {string} - The classified error type
 */
export const classifyError = (error) => {
    if (!error) return ERROR_TYPES.UNKNOWN;

    const errorString = typeof error === 'string' ? error : error.toString();
    const errorLower = errorString.toLowerCase();

    // OTP-specific errors (check before generic rate limit)
    if (errorLower.includes('auth_otp_send_cooldown')) {
        return ERROR_TYPES.OTP_SEND_COOLDOWN;
    }

    if (errorLower.includes('auth_otp_send_rate_limited')) {
        return ERROR_TYPES.OTP_SEND_RATE_LIMITED;
    }

    if (errorLower.includes('auth_otp_locked')) {
        return ERROR_TYPES.OTP_LOCKED;
    }

    if (errorLower.includes('auth_otp_expired')) {
        return ERROR_TYPES.OTP_EXPIRED;
    }

    if (errorLower.includes('auth_otp_invalid')) {
        return ERROR_TYPES.OTP_INVALID;
    }

    if (errorLower.includes('auth_failed')) {
        return ERROR_TYPES.AUTHENTICATION;
    }

    if (errorLower.includes('captcha')) {
        return ERROR_TYPES.CAPTCHA;
    }

    // Rate limiting errors (check this first as it's most specific)
    if (
        errorString.includes('Max login attempts reached') ||
        errorString.includes('must reset your password to login') ||
        errorLower.includes('tentativas máximas atingidas') ||
        errorLower.includes('deve redefinir sua senha') ||
        errorLower.includes('429') ||
        errorLower.includes('too many requests') ||
        errorLower.includes('muitas tentativas') ||
        errorLower.includes('rate limit')
    ) {
        return ERROR_TYPES.RATE_LIMIT;
    }

    // Network/Connection errors
    if (
        errorLower.includes('network') ||
        errorLower.includes('conexão') ||
        errorLower.includes('connection') ||
        errorLower.includes('failed to fetch') ||
        errorLower.includes('networkerror') ||
        errorLower.includes('timeout') ||
        errorString.includes('NetworkError') ||
        errorString.includes('ERR_NETWORK') ||
        errorString.includes('ERR_INTERNET_DISCONNECTED')
    ) {
        return ERROR_TYPES.NETWORK;
    }

    // Server/Service errors
    if (
        errorLower.includes('server') ||
        errorLower.includes('servidor') ||
        errorLower.includes('service unavailable') ||
        errorLower.includes('serviço indisponível') ||
        errorLower.includes('internal server error') ||
        errorLower.includes('502') ||
        errorLower.includes('503') ||
        errorLower.includes('504') ||
        errorString.includes('500')
    ) {
        return ERROR_TYPES.SERVER;
    }


    // Authentication errors (these should come last to catch specific auth errors)
    if (
        errorLower.includes('authentication failed') ||
        errorLower.includes('e-mail ou senha inválidos') ||
        errorLower.includes('invalid password') ||
        errorLower.includes('senha incorreta') ||
        errorLower.includes('incorrect password') ||
        errorLower.includes('invalid credentials') ||
        errorLower.includes('credenciais inválidas') ||
        errorString.includes('auth:login-failure')
    ) {
        return ERROR_TYPES.AUTHENTICATION;
    }

    return ERROR_TYPES.UNKNOWN;
};

/**
 * Gets the appropriate error message key based on error type
 * @param {string} errorType - The classified error type
 * @returns {string} - The i18n key for the error message
 */
export const getErrorMessageKey = (errorType) => {
    switch (errorType) {
        case ERROR_TYPES.AUTHENTICATION:
            return 'VALIDATIONS.INCORRECT_PASSWORD';
        case ERROR_TYPES.NETWORK:
            return 'VALIDATIONS.NETWORK_ERROR';
        case ERROR_TYPES.SERVER:
            return 'VALIDATIONS.SERVER_ERROR';
        case ERROR_TYPES.RATE_LIMIT:
            return 'VALIDATIONS.INCORRECT_PASSWORD';
        case ERROR_TYPES.OTP_INVALID:
            return 'VALIDATIONS.OTP_INVALID';
        case ERROR_TYPES.OTP_EXPIRED:
            return 'VALIDATIONS.OTP_EXPIRED';
        case ERROR_TYPES.OTP_LOCKED:
            return 'VALIDATIONS.OTP_LOCKED';
        case ERROR_TYPES.OTP_SEND_COOLDOWN:
            return 'VALIDATIONS.OTP_SEND_COOLDOWN';
        case ERROR_TYPES.OTP_SEND_RATE_LIMITED:
            return 'VALIDATIONS.OTP_SEND_RATE_LIMITED';
        case ERROR_TYPES.CAPTCHA:
            return 'VALIDATIONS.CAPTCHA_ERROR';
        default:
            return 'VALIDATIONS.GENERIC_ERROR';
    }
};

/**
 * Gets the message key shown on the password (legacy) step when the AWA flow
 * falls back to password login. Uses the reason code returned by the backend
 * (e.g. AUTH_OTP_EXPIRED) so the user understands why the code step was skipped.
 * @param {string} errorType - The classified error type from the fallback reason
 * @returns {string} - The i18n key for the fallback message
 */
export const getLegacyFallbackMessageKey = (errorType) => {
    switch (errorType) {
        case ERROR_TYPES.OTP_EXPIRED:
            return 'VALIDATIONS.OTP_FALLBACK_EXPIRED';
        case ERROR_TYPES.OTP_LOCKED:
            return 'VALIDATIONS.OTP_FALLBACK_LOCKED';
        default:
            return 'VALIDATIONS.OTP_FALLBACK_GENERIC';
    }
};

/**
 * Resend cooldown duration (in seconds) for each OTP throttling error type.
 * COOLDOWN is a short wait between requests; RATE_LIMITED is a longer block
 * after too many requests.
 */
export const OTP_RESEND_COOLDOWN_SECONDS = {
    [ERROR_TYPES.OTP_SEND_COOLDOWN]: 60,
    [ERROR_TYPES.OTP_SEND_RATE_LIMITED]: 15 * 60,
};

/**
 * Default cooldown applied to a normal OTP send (no throttling).
 */
export const OTP_RESEND_DEFAULT_SECONDS = 60;

/**
 * Gets the resend countdown duration (seconds) for a given error type.
 * Falls back to the default cooldown when the type isn't a throttling error.
 * @param {string} errorType - The classified error type
 * @param {number} fallback - Duration used when no specific mapping exists
 * @returns {number} - Countdown duration in seconds
 */
export const getOtpResendCooldownSeconds = (errorType, fallback = OTP_RESEND_DEFAULT_SECONDS) => {
    return OTP_RESEND_COOLDOWN_SECONDS[errorType] || fallback;
};

/**
 * Determines if an error type represents OTP send throttling (cooldown or
 * rate limit), which should surface a message and drive the resend timer.
 * @param {string} errorType - The classified error type
 * @returns {boolean} - Whether the error is an OTP send throttling error
 */
export const isOtpSendThrottled = (errorType) => {
    return (
        errorType === ERROR_TYPES.OTP_SEND_COOLDOWN ||
        errorType === ERROR_TYPES.OTP_SEND_RATE_LIMITED
    );
};

/**
 * Determines if error should show password recovery option
 * @param {string} errorType - The classified error type
 * @returns {boolean} - Whether to show password recovery option
 */
export const shouldShowPasswordRecovery = (errorType) => {
    return errorType === ERROR_TYPES.AUTHENTICATION || errorType === ERROR_TYPES.RATE_LIMIT;
};

/**
 * Determines if error should show remaining attempts
 * @param {string} errorType - The classified error type
 * @returns {boolean} - Whether to show remaining attempts
 */
export const shouldShowRemainingAttempts = (errorType) => {
    return errorType === ERROR_TYPES.AUTHENTICATION || errorType === ERROR_TYPES.RATE_LIMIT;
};
